import { useCallback, useRef, useEffect } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
}

export function RichTextEditor({
  value,
  onChange,
  maxLength = 2000,
  placeholder = 'Enter content...',
  id,
  'aria-label': ariaLabel,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);

  // Only update innerHTML from external value changes (not from typing)
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const textContent = editorRef.current.textContent ?? '';
      if (maxLength && textContent.length > maxLength) {
        return;
      }
      isInternalChange.current = true;
      onChange(html === '<br>' ? '' : html);
    }
  }, [onChange, maxLength]);

  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  const handleBold = useCallback(() => execCommand('bold'), [execCommand]);
  const handleItalic = useCallback(() => execCommand('italic'), [execCommand]);
  const handleUnorderedList = useCallback(() => execCommand('insertUnorderedList'), [execCommand]);

  const handleLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  }, [execCommand]);

  const textLength = editorRef.current?.textContent?.length ?? 0;

  return (
    <div className="rich-text-editor" data-testid="rich-text-editor">
      <div className="rich-text-editor__toolbar" role="toolbar" aria-label="Text formatting">
        <button
          type="button"
          className="rich-text-editor__btn"
          onClick={handleBold}
          title="Bold"
          aria-label="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className="rich-text-editor__btn"
          onClick={handleItalic}
          title="Italic"
          aria-label="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className="rich-text-editor__btn"
          onClick={handleLink}
          title="Insert link"
          aria-label="Insert link"
        >
          🔗
        </button>
        <button
          type="button"
          className="rich-text-editor__btn"
          onClick={handleUnorderedList}
          title="Bullet list"
          aria-label="Bullet list"
        >
          • List
        </button>
      </div>
      <div
        ref={editorRef}
        id={id}
        className="rich-text-editor__content"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel ?? 'Rich text editor'}
        data-placeholder={placeholder}
        onInput={handleInput}
      />
      {maxLength && (
        <div className="rich-text-editor__counter" aria-live="polite">
          {textLength}/{maxLength}
        </div>
      )}
    </div>
  );
}
